// Real engines, numbers from the research notes with their sources. Unit conversions happen here in code.
// rpm convention: pistons, turboprops (propeller shaft) and marine engines carry real crankshaft/propeller rpm.
// Turbofans carry an N-scale label: SimState.rpm = 2000 + 8000 × load, matching the audio banks' 2000–10000
// loop labels; their ratedRpm/idleRpm are 10000/2000 on that scale.
// Every value without a published source says "estimate" in its comment.
import type { EngineSpec } from '../types';
import { hpToKw, lbToKg, lbfToN, bsfcFromLbPerHpH, tsfcFromLbPerLbfH, gPerKwhToKg, ftToM } from '../physics/atmosphere';
import { SRC } from './sources';

/** Leeham News: installed TSFC runs 7–8 % above the test-stand figure (nacelle losses, power offtake). */
const INSTALLATION_TSFC = 1.075;

export const ENGINES: EngineSpec[] = [
  {
    id: 'o-300-d',
    name: 'Continental O-300-D',
    kind: 'piston',
    ratedPowerKw: hpToKw(145), // 145 hp at 2,700 rpm, 301.4 in³ flat-six (Wikipedia Continental O-300)
    dryMassKg: lbToKg(268), // 268 lb without starter or generator (Wikipedia)
    bsfcKgPerKwh: bsfcFromLbPerHpH(0.5), // estimate: top of the report's 0.45–0.50 lb/hp·h best-power band; the 172F brochure's 36 gal in 4.2 h at 75 % implies 0.47 at its lean cruise setting
    fuelInjected: false, // carburetted: no lean-of-peak operation
    propeller: 'fixed',
    ratedRpm: 2700,
    idleRpm: 600, // estimate
    audio: 'verified',
    audioBank: 'o-300-d',
    audioNote: 'Recording: onboard a 1964 Cessna 172E (Continental O-300-D)',
    photo: 'o-300-d',
    sources: [SRC.wikiO300, SRC.wikiC172, SRC.lycomingSI1094],
  },
  {
    id: 'merlin-v1650',
    name: 'Packard V-1650-7 Merlin (Merlin 66 class)',
    kind: 'piston',
    ratedPowerKw: hpToKw(1490), // 1,490 hp at 3,000 rpm military power (Wikipedia P-51D specifications)
    dryMassKg: lbToKg(1640), // Merlin 61 dry weight 1,640 lb (Jane's via Wikipedia) standing in for the V-1650-7
    bsfcKgPerKwh: bsfcFromLbPerHpH(0.53), // estimate: no primary Merlin SFC exists; Jane's 130 Imp gal/h at 1,565 hp is 0.60 lb/hp·h at full power, and the 6:1 supercharged engine sits above the modern 0.45–0.50 best-power band
    criticalAltM: ftToM(12250), // rated power held to 12,250 ft in MS supercharger gear (Merlin 61 data, Wikipedia)
    fuelInjected: false, // twin-choke S.U. carburettor with automatic mixture control
    propeller: 'constant-speed',
    ratedRpm: 3000,
    idleRpm: 600, // estimate
    audio: 'verified',
    audioBank: 'merlin-v1650',
    photo: 'merlin-v1650',
    sources: [SRC.wikiMerlin, SRC.wikiP51],
  },
  {
    id: 'cfm56-7b26',
    name: 'CFM International CFM56-7B26',
    kind: 'turbofan',
    ratedThrustN: lbfToN(26300), // 26,300 lbf takeoff (Wikipedia CFM56 table)
    dryMassKg: lbToKg(5216), // 5,216 lb dry
    tsfcKgPerNh: tsfcFromLbPerLbfH(0.62), // estimate: Leeham puts CFM56-class cruise TSFC at 0.60–0.65 lb/lbf·h installed; the only published figure is the -5B4's 0.545 uninstalled
    ratedRpm: 10000, // N-scale label (see header); 100 % N1 is 5,175 rpm on the CFM56-7B
    idleRpm: 2000,
    audio: 'verified',
    audioBank: 'cfm56-7b26',
    audioNote: 'Cruise loop recorded on a 737-800 (CFM56-7B); take-off loop on a 737 Classic (CFM56-3)',
    photo: 'cfm56-7b26',
    sources: [SRC.wikiCfm56, SRC.leeham],
  },
  {
    id: 'f100-pw-229',
    name: 'Pratt & Whitney F100-PW-229',
    kind: 'turbofan',
    ratedThrustN: lbfToN(17800), // 17,800 lbf intermediate (dry)
    afterburnerThrustN: lbfToN(29160), // 29,160 lbf with afterburner
    dryMassKg: lbToKg(3829),
    tsfcKgPerNh: tsfcFromLbPerLbfH(0.76) * 1.1 * INSTALLATION_TSFC, // estimate: 0.76 is the sea-level static dry figure; ×1.1 for the Mach/altitude rise of a low-bypass engine at M 0.8 (Mattingly-type model), +7.5 % installed
    tsfcAfterburnerKgPerNh: tsfcFromLbPerLbfH(1.94),
    ratedRpm: 10000, // N-scale label (see header)
    idleRpm: 2000,
    audio: 'verified',
    audioBank: 'f100-pw-229',
    audioNote: 'Recording: Belgian F-16AM with F100-PW-220E, same engine family',
    photo: 'f100-pw-229',
    sources: [SRC.wikiF100, SRC.leeham],
  },
  {
    id: 'yanmar-2gm20',
    name: 'Yanmar 2GM20',
    kind: 'diesel',
    ratedPowerKw: 13.4, // 13.4 kW (18 hp) at 3,600 rpm; continuous 11.8 kW at 3,400 rpm (Wikipedia, Yanmar sheet)
    dryMassKg: 114, // about 114 kg (Wikipedia); the LSM datasheet gives 106 kg for the raw-water-cooled version
    bsfcKgPerKwh: gPerKwhToKg(260), // estimate: upper end of the ship notes' 240–260 g/kWh small-sailboat-diesel band
    ratedRpm: 3600,
    idleRpm: 850, // estimate
    audio: 'verified',
    audioBank: 'yanmar-2gm20',
    audioNote: 'Recording: a two-cylinder Yanmar marine diesel; the recordist does not state the model',
    photo: 'yanmar-2gm20',
    sources: [SRC.wikiYanmar2gm20, SRC.yanmar2gm20Sheet, SRC.cbratsYanmar],
  },
  {
    id: 'volvo-md22',
    name: 'Volvo Penta MD22P',
    kind: 'diesel',
    ratedPowerKw: 43.6, // 43.6 kW (59 hp) crankshaft, full-throttle range 3,500–4,000 rpm (Volvo Penta sheet)
    dryMassKg: 243, // 243 kg with the MS25L reverse gear, the lightest configuration on the sheet
    bsfcKgPerKwh: gPerKwhToKg(235), // estimate: high-speed marine diesel band 224–235 g/kWh (Cummins QSB6.7 bulletin, MTU 4000)
    ratedRpm: 4000,
    idleRpm: 800, // MD22 instruction book: idling speed 775–825 rpm
    audio: 'pending',
    audioNote: 'Only an idle recording exists; a cruise-rpm recording is still needed',
    photo: 'volvo-md22',
    sources: [SRC.volvoMd22p, SRC.volvoMd22Book, SRC.cumminsQsb],
  },
  {
    id: 'yamaha-f300',
    name: 'Yamaha F300 (4.2 L V6 four-stroke outboard)',
    kind: 'outboard',
    ratedPowerKw: hpToKw(300), // 300 hp class; peak output at 5,500 rpm, full-throttle range 5,000–6,000 rpm
    dryMassKg: lbToKg(562), // Yamaha bulletin "weight (estimated) 562 lb" for the F300XCA
    bsfcKgPerKwh: 0.3, // estimate: the Hurricane bulletin's 25.5 gal/h at 5,800 rpm is 0.32 kg/kWh at 0.74 kg/L and 300 hp
    ratedRpm: 5500,
    idleRpm: 650, // trolling range 600–1,000 rpm (dealer listing); estimate within it
    audio: 'pending',
    audioNote: 'The only free outboard recording does not state the engine type',
    photo: 'yamaha-f300',
    sources: [SRC.yamahaHurricane, SRC.yamahaAquaPatio, SRC.yamahaRegal],
  },
  {
    id: 'pt6a-114a',
    name: 'Pratt & Whitney Canada PT6A-114A',
    kind: 'turboprop',
    ratedPowerKw: hpToKw(675), // 675 shp flat-rated (EASA TCDS Cessna 208)
    dryMassKg: 163, // UK CAA TCDS
    bsfcKgPerKwh: bsfcFromLbPerHpH(0.64), // Wikipedia PT6 table (sea-level static, uncited there)
    criticalAltM: ftToM(10000), // estimate: flat rating held to roughly 10,000 ft on a standard day
    propeller: 'constant-speed',
    ratedRpm: 1900, // propeller 1,865/1,970 rpm (EASA TCDS)
    idleRpm: 1000, // estimate: propeller idle
    audio: 'pending',
    audioNote: 'No PT6A recording with a usable licence names the engine; the King Air exterior clip is unlabelled',
    photo: 'pt6a-114a',
    sources: [SRC.wikiPt6, SRC.caaPt6a114a, SRC.easaC208],
  },
  {
    id: 'lm2500',
    name: 'GE LM2500',
    kind: 'gas-turbine',
    ratedPowerKw: 22070, // US Navy standard day (100 °F, inlet/exhaust losses); ISO 25,060 kW
    dryMassKg: lbToKg(40500), // 40,500 lb (18.4 t) module
    bsfcKgPerKwh: gPerKwhToKg(233), // 0.383 lb/shp·h Navy standard day
    ratedRpm: 3600, // power turbine
    idleRpm: 1500, // estimate
    audio: 'pending',
    audioNote: 'No LM2500-class recording with a usable licence; the nearest free clip is a tank gas turbine',
    photo: 'lm2500',
    sources: [SRC.geLm2500],
  },
  {
    id: 'rt-flex96c',
    name: 'Wärtsilä-Sulzer 14RT-flex96C',
    kind: 'two-stroke-diesel',
    ratedPowerKw: 80080, // 80,080 kW at 102 rpm
    dryMassKg: 2300000, // "weighs over 2,300 t" (Wikipedia RTA96-C)
    bsfcKgPerKwh: gPerKwhToKg(171), // 171 g/kWh at 85 % load, R2 point (Wikipedia); secondary sources quote 0.260 lb/hp·h = 158 g/kWh
    ratedRpm: 102,
    idleRpm: 15, // technical data: 15–102 rpm
    audio: 'pending',
    audioNote: 'No slow-speed two-stroke recording with a usable licence; the nearest is an icebreaker engine room (CC BY 4.0)',
    photo: 'rt-flex96c',
    sources: [SRC.wikiRta96c, SRC.proMariner],
  },
  {
    id: 'ge90-115b',
    name: 'GE Aerospace GE90-115B',
    kind: 'turbofan',
    ratedThrustN: lbfToN(115540), // 115,540 lbf
    dryMassKg: lbToKg(19316),
    tsfcKgPerNh: tsfcFromLbPerLbfH(0.52) * INSTALLATION_TSFC, // estimate: the -115B cruise TSFC is unpublished; the -85B's 0.520 lb/lbf·h is used, +7.5 % installed
    ratedRpm: 10000, // N-scale label (see header)
    idleRpm: 2000,
    audio: 'pending',
    audioNote: 'The one CC0 GE90 clip (777F take-off, 49 s) is too short for steady loops',
    photo: 'ge90-115b',
    sources: [SRC.wikiGe90, SRC.leeham],
  },
];

export function engineById(id: string): EngineSpec | undefined {
  return ENGINES.find((e) => e.id === id);
}
